import { useEffect, useMemo, useState } from "react";
import WebApp from "@twa-dev/sdk";
import type { ThemeParams, WebApp as WebAppType, WebAppInitData } from "@twa-dev/types";
import { getPlatform } from "@/lib/platform";

export const tg: WebAppType = WebApp;

export function isTelegramEnvironment(): boolean {
  return typeof tg.initData === "string" && tg.initData.length > 0;
}

export function useTelegramReady(): void {
  useEffect(() => {
    try {
      tg.ready();
      tg.expand();
      tg.enableClosingConfirmation();
    } catch {
      // Non-Telegram browsers: the polyfill occasionally throws on ready(); safe to swallow.
    }
  }, []);
}

/** Subscribe to focus/visibility transitions so the caller can refresh
 *  state that may have been mutated server-side while the mini-app was
 *  backgrounded (admin grant, payment webhook, refund).
 *
 *  React Query already does this via `refetchOnWindowFocus`, but the
 *  Telegram WebView has historically swallowed the synthetic `focus`
 *  event under iOS — this hook listens to both `visibilitychange` and
 *  `focus` so at least one path fires when the user swipes back in.
 *
 *  The handler is invoked once per transition into "visible & focused";
 *  it does NOT fire on the initial mount (that's the caller's first
 *  query). */
export function useOnAppFocus(handler: () => void): void {
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === "visible") handler();
    };
    const onFocus = (): void => {
      handler();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [handler]);
}

interface UseBackButtonOptions {
  visible: boolean;
  onClick?: () => void;
}

export function useBackButton({ visible, onClick }: UseBackButtonOptions): void {
  useEffect(() => {
    if (!visible) {
      // A prior `show()` call's cleanup already hides on visible:true ->
      // false transitions, but a component can also mount directly with
      // visible:false (e.g. BackHeader on /home) with no prior `show()` to
      // have cleaned up after. Explicitly hide here too — matching
      // `mainButton.set()`'s own `!visible` branch — to guarantee no
      // leftover BackButton state survives a fresh mount. `tg.BackButton`
      // is a safe no-op outside a real Telegram client (never throws), but
      // it does log a "BackButton is not supported" console warning on iOS
      // and on the web (where the SDK reports version 6.0 / is absent).
      // Skip it there: the platform adapter's own cleanup
      // (`useIosUiStore.showBack`'s returned fn, driven by
      // `getPlatform().backButton.show()` below) already reset the store,
      // so this call only needs to quiet the native Telegram button on
      // Telegram itself (DUE-318).
      if (getPlatform().name === "telegram") {
        tg.BackButton.hide();
      }
      return;
    }
    const handler = onClick ?? ((): void => history.back());
    return getPlatform().backButton.show(handler);
  }, [visible, onClick]);
}

interface UseMainButtonOptions {
  text: string;
  onClick: () => void;
  visible?: boolean;
  loading?: boolean;
  disabled?: boolean;
}

export function useMainButton({
  text,
  onClick,
  visible = true,
  loading = false,
  disabled = false,
}: UseMainButtonOptions): void {
  useEffect(() => {
    return getPlatform().mainButton.set({ text, onClick, visible, loading, disabled });
  }, [text, onClick, visible, loading, disabled]);
}

/** Suppress the SDK MainButton on screens that render their own DS-styled
 *  CTA. Without this, leftover state from a previous screen would keep
 *  the native blue button visible. */
export function useHideMainButton(): void {
  useMainButton({ text: "", visible: false, onClick: noop });
}

function noop(): void {}

export interface TelegramTheme {
  colorScheme: "light" | "dark";
  themeParams: ThemeParams;
}

export function useTelegramTheme(): TelegramTheme {
  const [theme, setTheme] = useState<TelegramTheme>(() => ({
    colorScheme: tg.colorScheme,
    themeParams: tg.themeParams,
  }));
  useEffect(() => {
    const handler = (): void => {
      setTheme({ colorScheme: tg.colorScheme, themeParams: tg.themeParams });
    };
    tg.onEvent("themeChanged", handler);
    return () => tg.offEvent("themeChanged", handler);
  }, []);
  return theme;
}

type HapticImpact = "light" | "medium" | "heavy" | "rigid" | "soft";
type HapticNotification = "error" | "success" | "warning";

export interface HapticApi {
  impact: (style: HapticImpact) => void;
  notification: (type: HapticNotification) => void;
  selectionChanged: () => void;
}

/** Module-level singleton so `useHapticFeedback` and the Telegram Platform
 *  adapter (`lib/platform/telegram.ts`) share one object instead of each
 *  building their own wrapper. */
export const telegramHaptics: HapticApi = {
  impact: (style) => {
    try {
      tg.HapticFeedback.impactOccurred(style);
    } catch {
      /* not available in dev */
    }
  },
  notification: (type) => {
    try {
      tg.HapticFeedback.notificationOccurred(type);
    } catch {
      /* not available in dev */
    }
  },
  selectionChanged: () => {
    try {
      tg.HapticFeedback.selectionChanged();
    } catch {
      /* not available in dev */
    }
  },
};

export function useHapticFeedback(): HapticApi {
  return getPlatform().haptics;
}

export interface InitData {
  initData: string;
  initDataUnsafe: WebAppInitData;
}

export function useInitData(): InitData {
  return useMemo(
    () => ({
      initData: tg.initData,
      initDataUnsafe: tg.initDataUnsafe,
    }),
    [],
  );
}

export interface AsyncKeyValueStorage {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
  getKeys: () => Promise<string[]>;
}

/**
 * Device-local key/value storage, backed by `window.localStorage`.
 *
 * DUE-196: this is what the Telegram/web `Platform.storage` seam resolves
 * to. Values written here never leave the device — unlike `cloudStorage`
 * below, which round-trips through Telegram's servers. It doubles as the
 * fallback `cloudStorage` degrades to when the CloudStorage bridge is
 * unavailable or hangs.
 */
export const deviceStorage: AsyncKeyValueStorage = {
  getItem: async (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: async (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* storage unavailable */
    }
  },
  removeItem: async (key) => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* storage unavailable */
    }
  },
  getKeys: async () => {
    try {
      return Object.keys(window.localStorage);
    } catch {
      return [];
    }
  },
};

/** True when the running Telegram client exposes a usable CloudStorage
 *  bridge. Exported for `lib/storageMigration.ts`, which must know whether
 *  there is a cloud copy to purge at all. */
export function isTelegramCloudStorageAvailable(): boolean {
  return (
    typeof tg.CloudStorage?.getItem === "function" &&
    typeof tg.isVersionAtLeast === "function" &&
    tg.isVersionAtLeast("6.9")
  );
}

const telegramCloudStorageDirect: AsyncKeyValueStorage = {
  getItem: (key) =>
    new Promise((resolve, reject) => {
      tg.CloudStorage.getItem(key, (error, result) => {
        if (error) reject(new Error(error));
        else resolve(result ?? null);
      });
    }),
  setItem: (key, value) =>
    new Promise((resolve, reject) => {
      tg.CloudStorage.setItem(key, value, (error) => {
        if (error) reject(new Error(error));
        else resolve();
      });
    }),
  removeItem: (key) =>
    new Promise((resolve, reject) => {
      tg.CloudStorage.removeItem(key, (error) => {
        if (error) reject(new Error(error));
        else resolve();
      });
    }),
  getKeys: () =>
    new Promise((resolve, reject) => {
      tg.CloudStorage.getKeys((error, result) => {
        if (error) reject(new Error(error));
        else resolve(result ?? []);
      });
    }),
};

// Telegram CloudStorage callbacks occasionally never fire — observed on
// Telegram Desktop when the webview opened before the bridge was fully
// initialised. Wrap every read with a timeout so the mini app cannot hang
// on a bootstrap storage call (i18n detector, session hydration). On
// timeout we fall through to localStorage so the user still lands on the
// UI instead of an infinite spinner.
const CLOUD_STORAGE_TIMEOUT_MS = 2000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("cloud_storage_timeout")), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

async function raceOrFallback<T>(
  tgCall: () => Promise<T>,
  fallback: () => Promise<T>,
): Promise<T> {
  try {
    return await withTimeout(tgCall(), CLOUD_STORAGE_TIMEOUT_MS);
  } catch {
    return fallback();
  }
}

/**
 * Telegram CloudStorage with the hang guard but **no** localStorage
 * fallback: a failed/timed-out call rejects instead of silently answering
 * from the device.
 *
 * `lib/storageMigration.ts` needs exactly that. Migrating with the
 * fallback-flavoured `cloudStorage` below would be unsafe — a timed-out
 * `removeItem` degrades into `localStorage.removeItem`, which would delete
 * the very copy the migration just wrote to the device.
 */
export const telegramCloudStorage: AsyncKeyValueStorage = {
  getItem: (key) => withTimeout(telegramCloudStorageDirect.getItem(key), CLOUD_STORAGE_TIMEOUT_MS),
  setItem: (key, value) =>
    withTimeout(telegramCloudStorageDirect.setItem(key, value), CLOUD_STORAGE_TIMEOUT_MS),
  removeItem: (key) =>
    withTimeout(telegramCloudStorageDirect.removeItem(key), CLOUD_STORAGE_TIMEOUT_MS),
  getKeys: () => withTimeout(telegramCloudStorageDirect.getKeys(), CLOUD_STORAGE_TIMEOUT_MS),
};

/**
 * Cross-device storage — **values written here are stored on Telegram's
 * servers** (UAE jurisdiction), not on the device.
 *
 * DUE-196: this is deliberately NOT reachable through the `Platform` seam
 * any more. `Platform.storage` / `Platform.secureStorage` are device-local
 * on every platform; a caller that genuinely wants cross-device sync has to
 * import this symbol explicitly, which makes every such call site greppable
 * and reviewable. The only remaining product consumer is
 * `hooks/useLatestNatalReading.ts` (a reading row id + a random idempotency
 * UUID — no personal data, and cross-device continuity there is
 * load-bearing: a per-device idempotency key would mint duplicate paid
 * readings). Anything carrying personal data must use `deviceStorage`.
 */
export const cloudStorage: AsyncKeyValueStorage = {
  getItem: (key) =>
    isTelegramCloudStorageAvailable()
      ? raceOrFallback(
          () => telegramCloudStorageDirect.getItem(key),
          () => deviceStorage.getItem(key),
        )
      : deviceStorage.getItem(key),
  setItem: (key, value) =>
    isTelegramCloudStorageAvailable()
      ? raceOrFallback(
          () => telegramCloudStorageDirect.setItem(key, value),
          () => deviceStorage.setItem(key, value),
        )
      : deviceStorage.setItem(key, value),
  removeItem: (key) =>
    isTelegramCloudStorageAvailable()
      ? raceOrFallback(
          () => telegramCloudStorageDirect.removeItem(key),
          () => deviceStorage.removeItem(key),
        )
      : deviceStorage.removeItem(key),
  getKeys: () =>
    isTelegramCloudStorageAvailable()
      ? raceOrFallback(
          () => telegramCloudStorageDirect.getKeys(),
          () => deviceStorage.getKeys(),
        )
      : deviceStorage.getKeys(),
};
